// Carteira de faturamento (#6242 "Dividir a carteira de faturas por nível de dificuldade",
// pedido do Marcus Vinicius e do Felipe, reunião de 08/10/2026). Decisões do Victor, 22/10/2026:
//
//   - peso por fatura: B comum e avulso 1; GD2 2; grupo A, tarifa branca e A optante B 3;
//   - risco (cliente com fatura vencida nos últimos 3 meses) soma 1 ao cliente;
//   - a carteira é por cliente (CPF/CNPJ) e fixa: quem já tem dono continua com ele, e só
//     cliente novo ou de quem saiu do time é redistribuído — "Redividir tudo" é ação explícita;
//   - a divisão equilibra a soma dos pesos e, no empate, o valor em reais.
//
// Função pura, sem I/O: roda sob `node --test` com type-stripping.

export interface FaturaParaPeso {
  classificacao?: string | null;
  tarifa_parc_injet_s_desc_gd2?: number | null;
  energia_injetada_gd2_kwh?: number | null;
}

export const PESO_RISCO = 1;

/** Dificuldade de uma fatura: 1 (B comum, avulso), 2 (GD2) ou 3 (grupo A, branca, A optante B). */
export function pesoDificuldade(f: FaturaParaPeso): number {
  const c = String(f.classificacao ?? '').toUpperCase().trim();
  if (c.startsWith('A ') || c.includes('BRANCA') || c.includes('OPTANTE')) return 3;
  if (Number(f.tarifa_parc_injet_s_desc_gd2) > 0 || Number(f.energia_injetada_gd2_kwh) > 0) return 2;
  return 1;
}

export const normalizarDocumento = (v?: string | null) => String(v ?? '').replace(/\D/g, '');

export interface FaturaDaCarteira extends FaturaParaPeso {
  cliente_cpf_cnpj?: string | null;
  cliente_nome?: string | null;
  valor_boleto_vigor?: number | null;
}

export interface ClienteCarteira {
  cpf: string;
  nome: string;
  faturas: number;
  peso: number;
  valor: number;
  risco: boolean;
}

/** Junta as faturas do mês por cliente. Fatura sem CPF/CNPJ fica de fora (não tem dono possível). */
export function agruparClientes(faturas: FaturaDaCarteira[], emRisco: Set<string>): ClienteCarteira[] {
  const porCpf = new Map<string, ClienteCarteira>();
  for (const f of faturas) {
    const cpf = normalizarDocumento(f.cliente_cpf_cnpj);
    if (!cpf) continue;
    const c = porCpf.get(cpf) ?? { cpf, nome: f.cliente_nome || '', faturas: 0, peso: 0, valor: 0, risco: emRisco.has(cpf) };
    c.faturas += 1;
    c.peso += pesoDificuldade(f);
    c.valor += Number(f.valor_boleto_vigor) || 0;
    if (!c.nome && f.cliente_nome) c.nome = f.cliente_nome;
    porCpf.set(cpf, c);
  }
  for (const c of porCpf.values()) if (c.risco) c.peso += PESO_RISCO;
  return [...porCpf.values()];
}

export type ModoDistribuicao = 'manter' | 'redividir';

/**
 * Dono de cada cliente. 'manter' preserva quem já tem dono no time e só distribui o resto;
 * 'redividir' começa do zero. Os clientes a distribuir vão do mais pesado ao mais leve para quem
 * está com menos carga — o jeito simples de terminar com somas parecidas.
 */
export function distribuir(
  clientes: ClienteCarteira[],
  equipe: string[],
  atual: Record<string, string>,
  modo: ModoDistribuicao,
): Record<string, string> {
  const resultado: Record<string, string> = {};
  if (equipe.length === 0) return resultado;
  const carga = new Map(equipe.map(id => [id, { peso: 0, valor: 0 }]));
  const livres: ClienteCarteira[] = [];

  for (const c of clientes) {
    const dono = atual[c.cpf];
    if (modo === 'manter' && dono && carga.has(dono)) {
      resultado[c.cpf] = dono;
      const k = carga.get(dono)!;
      k.peso += c.peso;
      k.valor += c.valor;
    } else {
      livres.push(c);
    }
  }

  livres.sort((a, b) => b.peso - a.peso || b.valor - a.valor || a.cpf.localeCompare(b.cpf));
  for (const c of livres) {
    let melhor = equipe[0];
    for (const id of equipe) {
      const a = carga.get(id)!, m = carga.get(melhor)!;
      if (a.peso < m.peso || (a.peso === m.peso && a.valor < m.valor)) melhor = id;
    }
    resultado[c.cpf] = melhor;
    const k = carga.get(melhor)!;
    k.peso += c.peso;
    k.valor += c.valor;
  }
  return resultado;
}

export interface ResumoPessoa {
  clientes: number;
  faturas: number;
  peso: number;
  valor: number;
}

export function resumoPorPessoa(clientes: ClienteCarteira[], mapa: Record<string, string>): Record<string, ResumoPessoa> {
  const r: Record<string, ResumoPessoa> = {};
  for (const c of clientes) {
    const dono = mapa[c.cpf];
    if (!dono) continue;
    const s = (r[dono] ??= { clientes: 0, faturas: 0, peso: 0, valor: 0 });
    s.clientes += 1;
    s.faturas += c.faturas;
    s.peso += c.peso;
    s.valor += c.valor;
  }
  return r;
}
